import { useEffect, useState } from "react";
import useAsyncEffect from "use-async-effect";
import {
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesGamesChooseGamePortalConfig
} from "../../../Api/gen";
import { FxIdSdk } from "../../../SDK/FxIdSdk";
import { IFxIdSdkAdapterSocialSettings, IFxIdSdkOnAccountChangedEvent } from "../../../SDK/FxIdSdkBaseAdapter";
import { useQueryClient } from "@tanstack/react-query";

export type IframeType =
	| "ok"
	| "vkplay"
	| "vkcom"
	| "fxid"
	| "yandex"
	| "ag"
	| "kg"
	| "fbgames"
	| "crazygames"
	| "ymobage"
	| "telegram"
	| "fxlauncher"
	| "fxnative"
	| "fbinstantgames"
	| "steam"

export const IframeTypeToSupportedPlatformMap: {
	[index in IframeType]: FxIdDomainStoreEnumsSupportedWebPublishingPlatform;
} = {
	ok: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Odnoklassniki,
	vkplay: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay,
	vkcom: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom,
	fxid: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxId,
	yandex: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.YandexGames,
	ag: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.AbsoluteGames,
	kg: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Kongregate,
	fbgames: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookGames,
	fbinstantgames: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames,
	crazygames: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.CrazyGames,
	ymobage: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.WizQ,
	telegram: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Telegram,
	fxlauncher: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxLauncher,
	fxnative: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxNative,
	steam: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Steam,
};

export const SupportedPlatformToIframeTypeMap: {
	[index in FxIdDomainStoreEnumsSupportedWebPublishingPlatform]: IframeType;
} = {
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.AbsoluteGames]: "ag",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxId]: "fxid",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Kongregate]: "kg",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.NUMBER_unknown_default_open_api]: "fxid",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Odnoklassniki]: "ok",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Unspecified]: "fxid",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontakteCom]: "vkcom",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.VkontaktePlay]: "vkplay",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.YandexGames]: "yandex",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookGames]: "fbgames",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames]: "fbinstantgames",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.CrazyGames]: "crazygames",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.WizQ]: "ymobage",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Telegram]: "telegram",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxLauncher]: "fxlauncher",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FxNative]: "fxnative",
	[FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Steam]: "steam",
};
export const useFxIdSdk = (
	game: string | undefined,
	adapter: IframeType,
	portalConfig: FxIdWebFeaturesGamesChooseGamePortalConfig
) => {
	const [initialized, setInitialized] = useState(false);
	const [socialSettings, setSocialSettings] = useState<IFxIdSdkAdapterSocialSettings>();
	const [currentAccountId, setCurrentAccountId] = useState(-1);
	const supportedPlatform = IframeTypeToSupportedPlatformMap[adapter];
	const queryClient = useQueryClient();

	const onAccountChange = (e: IFxIdSdkOnAccountChangedEvent) => {
		log.info("useFxIdSdk onAccountChange");
		log.info("Clearing query caches");
		queryClient.clear();

		log.info("Settings new account id");
		setCurrentAccountId(e.newAccountId);
	};

	useAsyncEffect(async () => {
		if (!game) return;

		if (window.FxIdSdk !== undefined) {
			log.info("%c🛑🛑🛑 Secondary initialization of FxId SDK! 🛑🛑🛑", "font-weight: bold; font-size: 20px;");

			log.error("SDK can be initialized only once", "\nCheck dups of useFxIdSdk");
			return;
		}

		log.info("Initializing FxIdSdk");
		window.FxIdSdk = new FxIdSdk(game, supportedPlatform, portalConfig);
		await window.FxIdSdk?.InitializeSocialAdapter();
		await window.FxIdSdk?.LoginToFxId();

		const socialSettings = await window.FxIdSdk?.SocialSettings();
		setSocialSettings(socialSettings);

		setInitialized(true);
	}, [game]);

	useEffect(() => {
		if (window.FxIdSdk == null) return;
		window.FxIdSdk.OnChangeAccount.subscribe(onAccountChange);

		return () => {
			if (window.FxIdSdk == null) return;
			window.FxIdSdk.OnChangeAccount.unsubscribe(onAccountChange);
		};
	});

	/** useAsyncEffect некорректно ведет себя с функцией очистки. Для нормальной работы нужен обычный эффект */
	useEffect(() => () => (window.FxIdSdk = undefined), []);

	return { initialized, socialSettings, currentAccountId };
};
